import type {
  Collection,
  CollectionKind,
  PatchAdminV1StoresStoreIdCollectionsIdBody,
  PostAdminV1StoresStoreIdCollectionsBody,
} from '@nucleus-commerce/api-client/admin';

import { emptyRules, fromRules, toRulesInput, type RuleValueKind } from '@/features/product-rules';
import { fromSeoOverride, toSeoOverride } from '@/features/seo';

import type { CollectionFormInput, CollectionFormOutput } from '../schemas/collection-form-schema';

const orNull = (v: string): string | null => (v === '' ? null : v);

export function emptyCollectionForm(kind: CollectionKind): CollectionFormInput {
  return {
    kind,
    title: '',
    handle: '',
    descriptionHtml: '',
    mediaId: null,
    seo: fromSeoOverride(undefined),
    sortOrder: kind === 'manual' ? 'manual' : 'newest',
    status: 'active',
    role: 'merchandising',
    eyebrow: '',
    productTypeId: null,
    rules: emptyRules(kind === 'automated'),
  };
}

export function fromCollection(
  row: Collection,
  kindOf: (field: string) => RuleValueKind,
): CollectionFormInput {
  return {
    kind: row.kind,
    title: row.title,
    handle: row.handle,
    descriptionHtml: row.descriptionHtml ?? '',
    mediaId: row.mediaId,
    seo: fromSeoOverride(row.seo ?? undefined),
    sortOrder: row.sortOrder,
    status: row.status,
    role: row.role,
    eyebrow: row.eyebrow ?? '',
    productTypeId: row.productTypeId,
    rules: fromRules(row.rules, kindOf),
  };
}

/**
 * Every SEO key travels (null where blank), so a save never drops canonical or noIndex. Only a
 * category collection links a product type; the API refuses the link on any other role.
 */
function sharedBody(values: CollectionFormOutput) {
  return {
    handle: values.handle,
    title: values.title,
    descriptionHtml: orNull(values.descriptionHtml),
    sortOrder: values.sortOrder,
    mediaId: values.mediaId,
    seo: toSeoOverride(values.seo),
    status: values.status,
    role: values.role,
    eyebrow: orNull(values.eyebrow),
    productTypeId: values.role === 'category' ? values.productTypeId : null,
  };
}

/** Rules travel only for automated collections; the backend rejects them on manual ones. */
export function toCollectionCreate(
  values: CollectionFormOutput,
  kindOf: (field: string) => RuleValueKind,
): PostAdminV1StoresStoreIdCollectionsBody {
  return {
    ...sharedBody(values),
    kind: values.kind,
    ...(values.kind === 'automated' ? { rules: toRulesInput(values.rules, kindOf) } : {}),
  };
}

export function toCollectionUpdate(
  values: CollectionFormOutput,
  kindOf: (field: string) => RuleValueKind,
): PatchAdminV1StoresStoreIdCollectionsIdBody {
  return {
    ...sharedBody(values),
    ...(values.kind === 'automated' ? { rules: toRulesInput(values.rules, kindOf) } : {}),
  };
}
